import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOffers } from '@/api/useOffers';
import { usePopupState } from '@/state/hooks/popup.state.hook';
import { calculateNextDate, getTimecodeLabel, processDailyBonusBlocks, TIME_LEFT_INTERVAL } from '@/lib/dailyBonusUtils';

export const useDailyBonusTimer = () => {
  const [timeLeftLabel, setTimeLeftLabel] = useState<string>('');
  const [secondsLeft, setSecondsLeft] = useState<number>();
  const { getOffers } = useOffers({});
  const { popupOffers, popUpStartHourUTC } = usePopupState();

  const updateSecondsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const dailyBonusBlocksNextDate = useMemo((): Date | null => {
    if (!popupOffers || popupOffers.length === 0 || popUpStartHourUTC == null) {
      return null;
    }

    const blocks = processDailyBonusBlocks(popupOffers);
    if (blocks.length === 0) {
      return null;
    }

    const currentDate = new Date();
    const nextDate = new Date(currentDate);
    nextDate.setUTCHours(popUpStartHourUTC, 0, 0);

    if (nextDate.getTime() < currentDate.getTime()) {
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    }

    const lastBlock = blocks[blocks.length - 1];

    return calculateNextDate(lastBlock, nextDate);
  }, [popupOffers, popUpStartHourUTC]);

  const updateRemainingTime = useCallback((): number | undefined => {
    if (!dailyBonusBlocksNextDate) return;
    const remainingTime = dailyBonusBlocksNextDate.getTime() - new Date().getTime();
    setSecondsLeft(remainingTime);

    return remainingTime;
  }, [dailyBonusBlocksNextDate]);

  useEffect(() => {
    if (!dailyBonusBlocksNextDate) return;

    const remainingTime = updateRemainingTime();

    if (remainingTime && remainingTime <= 0) {
      return;
    }

    updateSecondsIntervalRef.current = setInterval(
      () => setSecondsLeft((prev) => (prev ? prev - TIME_LEFT_INTERVAL : prev)),
      TIME_LEFT_INTERVAL,
    );

    return () => {
      if (updateSecondsIntervalRef.current) {
        clearInterval(updateSecondsIntervalRef.current);
        updateSecondsIntervalRef.current = null;
      }
    };
  }, [dailyBonusBlocksNextDate, updateRemainingTime]);

  useEffect(() => {
    if (secondsLeft === undefined) return;

    if (secondsLeft <= 0) {
      setTimeLeftLabel('');
      getOffers.refetch();

      if (updateSecondsIntervalRef.current) {
        clearInterval(updateSecondsIntervalRef.current);
        updateSecondsIntervalRef.current = null;
      }
    } else {
      setTimeLeftLabel(getTimecodeLabel(secondsLeft));
    }
  }, [secondsLeft]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (!document.hidden) {
        updateRemainingTime();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateRemainingTime]);

  return {
    timeLeftLabel,
  };
};
